// useReleases — the band-scoped releases HUB hook set, built on useBandQuery/
// useBandMutation so the keys are ALWAYS ['band', bandId, …] (the tenant in the key, the structural
// fix). The list feeds the hub grid + spotlight + smart-links panel; create lands a release in Drafts,
// stamped with the band's to-do template. The frozen `release-changed` signal (ac.ui.band.{bandId})
// invalidates ['band', bandId, 'releases'] + ['band', bandId, 'release'] via the invalidationRegistry.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

type ReleaseSummaryPaged = components["schemas"]["ReleaseSummaryPaged"];
type ReleaseSummary = components["schemas"]["ReleaseSummary"];
type ReleaseRead = components["schemas"]["ReleaseRead"];
type CreateReleaseRequest = components["schemas"]["CreateReleaseRequest"];

export type { ReleaseSummaryPaged, ReleaseSummary, ReleaseRead, CreateReleaseRequest };

// ---- query keys ----------------------------------------------------------------------------------------
// Exported so the invalidation registry + the detail hook agree on the shape (the guestListKey precedent).

/** The hub-list key prefix: `['band', bandId, 'releases']` (+ status filter). */
export const releasesPrefix = (bandId: string) => ["band", bandId, "releases"] as const;
/** One release's composed-command-center key: `['band', bandId, 'release', releaseId]`. */
export const releaseKey = (bandId: string, releaseId: string) =>
  ["band", bandId, "release", releaseId] as const;

// ---- queries -------------------------------------------------------------------------------------------

/** The hub-list filter: the status pill (`?status=`) drives it; the cursor page is grid-internal.*/
export interface ReleaseFilters {
  status?: string;
}

/**
 * `GET /bands/{bandId}/releases` — the hub list, role-gated to the set (releases:read). Keyed
 * ['band', bandId, 'releases', filters]; a 403 (no releases:read) surfaces as the query error → the tab's
 * access-denied empty state. `limit` is generous (the hub grid is not infinite-scrolled at go-live); the
 * `nextCursor` is carried for a future "load more".
 */
export function useReleases(filters: ReleaseFilters = {}) {
  const { api } = useApp();
  return useBandQuery<ReleaseSummaryPaged>(
    ["releases", filters],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/releases", {
        params: { path: { bandId }, query: { status: filters.status, limit: 60 } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000 },
  );
}

// ---- mutations -----------------------------------------------------------------------------------------

/**
 * `POST /bands/{bandId}/releases` — create a Single/EP/Album → 201 ReleaseRead. Lands in Drafts (the
 * server births it `draft`), and the server stamps the band's to-do template onto it — there is no per-release
 * opt-in, so nothing about the checklist rides this call. Server-confirm THEN repaint (the
 * modal shows a pending state and closes only on a persisted write — never a phantom success).
 * Invalidates the hub list so the new draft appears in the grid + the Drafts pill count.
 */
export function useCreateRelease() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<ReleaseRead, CreateReleaseRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/releases", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: releasesPrefix(bandId) });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't create the release")),
    },
  );
}
